<template>
    <kt-modal 
        :visible="recordVisible" 
        :title="recordTitle"
        :size="'modal-xl'" 
        @dismiss="onDismiss()"
    >
        <template #body>
            <work-order-form v-model="record"></work-order-form>
        </template>

        <template #footer>
            <div class="row w-100">
                <div class="col-md-6">
                    
                </div>
                <div class="col-md-6">
                    <div class="row">
                        <div :class="{ 'col-4': record.Id, 'col-6': !record.Id }">
                            <button @click="onDismiss()" class="btn btn-sm btn-outline-secondary form-control">Close</button>
                        </div>
                        <div class="col-4" v-if="record.Id">
                            <button @click="deleteRecord()" class="btn btn-sm btn-outline-danger form-control">Delete</button>
                        </div>
                        <div :class="{ 'col-4': record.Id, 'col-6': !record.Id }">
                            <button @click="saveRecord()" class="btn btn-sm btn-outline-primary form-control">Save</button>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </kt-modal>
</template>

<script lang="ts" setup>
import { ref, watch, onUnmounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import Api from "@/services/ApiService";
import Alert from "@/services/Alert";
import Emitter from "@/services/Emitter";
import { WorkOrderModel } from "@/models/WorkOrderModel";
import KtModal from "@/components/UI/KtModal.vue";
import WorkOrderForm from "@/components/WorkOrders/WorkOrderForm.vue";

const route = useRoute();
const router = useRouter();
const recordTitle = ref("Work Order");
const recordVisible = ref(true);
const unwatch: Array<Function> = [];

const record = ref<WorkOrderModel>(new WorkOrderModel());

const onDismiss = () => {
    recordVisible.value = false;
    setTimeout(() => {
        router.push({ path: "/workorders" });
    }, 100);
};

const saveRecord = async () => {
    try {
        // Validate
        if (!record.value.ContactId) {
            Alert.notify({ text: "Please select a client", type: "error" });
            return;
        }

        // Auto-generate invoice number if not provided
        if (!record.value.InvoiceNumber && record.value.Contact && record.value.Id) {
            const contactCode = record.value.Contact.Code || 'INV';
            record.value.InvoiceNumber = `${contactCode}-${record.value.Id}`;
        }

        // Save
        const saved = await Api.saveWorkOrder(record.value);
        
        // Update local record with saved data
        record.value = saved;
        
        // Auto-generate invoice number for new records (after first save)
        if (!saved.InvoiceNumber && saved.Contact && saved.Id) {
            const contactCode = saved.Contact.Code || 'INV';
            record.value.InvoiceNumber = `${contactCode}-${saved.Id}`;
            // Save again with the generated invoice number
            const resaved = await Api.saveWorkOrder(record.value);
            record.value = resaved;
        }
        
        Alert.notify({ text: "Work order saved successfully", type: "success" });
        
        // Refresh datatable if embedded
        Emitter.emit('datatable-refresh', 'workorder-list');
        Emitter.emit('datatable-refresh', 'workorder-embedded');
        
        // Update route if it was a new record
        if (route.params.id === 'new') {
            router.replace({ path: "/workorder/edit/" + saved.Id });
        }
    } catch (error) {
        console.error("Error saving work order:", error);
        Alert.notify({ text: "Failed to save work order", type: "error" });
    }
};

const deleteRecord = async () => {
    if (!confirm('Are you sure you want to delete this work order?')) {
        return;
    }

    try {
        await Api.deleteWorkOrder(record.value.Id!);
        Alert.notify({ text: "Work order deleted", type: "success" });
        
        // Refresh datatables
        Emitter.emit('datatable-refresh', 'workorder-list');
        Emitter.emit('datatable-refresh', 'workorder-embedded');
        
        onDismiss();
    } catch (error) {
        console.error("Error deleting work order:", error);
        Alert.notify({ text: "Failed to delete work order", type: "error" });
    }
};

// Watch for route param changes
unwatch.push(
    watch(
        () => route.params.id,
        async (newId) => {
            if (newId === 'new') {
                recordTitle.value = "New Work Order";
                record.value = new WorkOrderModel();
            } else if (newId) {
                try {
                    const data = await Api.loadWorkOrder(Number(newId));
                    record.value = data;
                    recordTitle.value = `Work Order #${data.InvoiceNumber || data.Id}`;
                } catch (error) {
                    console.error("Error loading work order:", error);
                    Alert.notify({ text: "Failed to load work order", type: "error" });
                }
            }
        },
        { immediate: true }
    )
);

onUnmounted(() => {
    unwatch.forEach(fn => fn());
});
</script>
